'use client';
import { useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { use, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '@/app/_trpc/client';
import Avatar from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { logger } from '@/lib/clientSideLogger';
import { DialogContext } from './infra/context/DialogContext';
import { DialogDefaultProps } from './infra/types';
const reasons = [
  'We are planning to meet later',
  "The other user didn't reply",
  "The other user didn't show up on time",
  "I didn't have time for this match anymore",
  'Another reason (please specify)',
];

export type HaveYouMetDialogProps = {
  match: {
    id: string;
  };
  user: {
    id: string;
    name: string;
    profilePhoto: string;
  };
};

export function useShouldShowHaveYouMetDialog(): {
  loading: boolean;
  shouldShow: boolean;
  props: HaveYouMetDialogProps | undefined;
} {
  const { data: session } = useSession();
  const { data: match, isLoading } =
    trpc.matchFeedback.getWeMetPopupMatch.useQuery();
  if (isLoading) return { loading: true, shouldShow: false, props: undefined };
  if (!match || !session)
    return { loading: false, shouldShow: false, props: undefined };
  const currentUserId = session.user.id;
  return {
    loading: false,
    shouldShow: true,
    props: {
      match: { id: match.id },
      user: {
        id:
          match.localUserId === currentUserId
            ? (match.refugeeUser?.id ?? '')
            : (match.localUser?.id ?? ''),
        profilePhoto:
          match.localUserId === currentUserId
            ? (match.refugeeUser?.profile?.profilePhoto ?? '')
            : (match.localUser?.profile?.profilePhoto ?? ''),
        name:
          match.localUserId === currentUserId
            ? (match.refugeeUser?.name ?? '')
            : (match.localUser?.name ?? ''),
      },
    },
  };
}

const HaveYouMetDialog = ({
  open,
  onOpenChange,
  match,
  user,
}: HaveYouMetDialogProps & DialogDefaultProps) => {
  const t = useTranslations('HaveYouMetDialog');

  const { openDialog } = use(DialogContext);
  const [isDidntMeet, setIsDidntMeet] = useState(false);
  const [matchFeedbackId, setMatchFeedbackId] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const createMatchFeedback = trpc.matchFeedback.create.useMutation({
    onSuccess: (data) => {
      setMatchFeedbackId(data.id);
    },
    onError: () => {
      toast.error(t('feedback-error'));
      onOpenChange(false);
    },
  });
  const updateMatchFeedback = trpc.matchFeedback.update.useMutation({
    onSuccess: () => {
      toast.success(t('feedback-sent'));
      onOpenChange(false);
    },
  });
  const updateUserDidNotShowUp =
    trpc.users.updateDidNotShowUpOnTime.useMutation();

  const saveWhyDidntMeet = () => {
    if (!matchFeedbackId) {
      const enhancedError = new Error(
        'No match feedback id found when updating match feedback'
      );
      logger('error', enhancedError);
      return;
    }
    updateMatchFeedback.mutate({
      id: matchFeedbackId,
      explanation: customReason ? customReason : selectedReason,
    });
    if (selectedReason === "The other user didn't show up on time") {
      updateUserDidNotShowUp.mutate({
        id: user.id,
      });
    }
  };
  const weDidntMeet = () => {
    createMatchFeedback.mutate({
      weMet: false,
      matchId: match.id,
    });
    setIsDidntMeet(true);
  };
  const weMet = () => {
    createMatchFeedback.mutate(
      {
        weMet: true,
        matchId: match.id,
      },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          openDialog('match-feedback', {
            userId: user.id,
            feedbackId: data.id,
          });
        },
      }
    );
  };
  const isPendingWhyDidntMet =
    updateMatchFeedback.isPending || updateUserDidNotShowUp.isPending;

  return (
    <Dialog open={open}>
      <DialogContent hideClose={true}>
        <DialogHeader>
          <DialogTitle>
            {!isDidntMeet ? t('title') : t('titleDidntMeet')}
          </DialogTitle>
          {!isDidntMeet && (
            <DialogDescription>
              {t('description', { userName: user?.name })}
            </DialogDescription>
          )}
        </DialogHeader>
        {isDidntMeet ? (
          <>
            <Select onValueChange={setSelectedReason} value={selectedReason}>
              <SelectTrigger>
                <SelectValue placeholder={t('select a reason')} />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedReason === 'Another reason (please specify)' && (
              <Textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                className='min-h-[100px] w-full'
                placeholder={t('pleaseSpecifyReason')}
              />
            )}
          </>
        ) : (
          <>
            <Avatar
              imageUrl={user?.profilePhoto ?? undefined}
              name={user?.name ?? undefined}
              variant='square'
              className='mx-auto mb-4 h-40 w-40'
            />
          </>
        )}

        <DialogFooter>
          <div className='flex w-full gap-4 [&>*]:w-full'>
            {isDidntMeet ? (
              <>
                <Button
                  onClick={saveWhyDidntMeet}
                  size='medium'
                  className='w-full'
                  variant='tertiary'
                  disabled={isPendingWhyDidntMet}
                  loading={isPendingWhyDidntMet}
                  loadingText={t('skip')}
                >
                  {t('skip')}
                </Button>
                <Button
                  onClick={saveWhyDidntMeet}
                  size='medium'
                  className='w-full'
                  disabled={isPendingWhyDidntMet}
                  loading={isPendingWhyDidntMet}
                  loadingText={t('send')}
                >
                  {t('send')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => weDidntMeet()}
                  size='medium'
                  variant='tertiary'
                  disabled={createMatchFeedback.isPending}
                  loading={createMatchFeedback.isPending}
                  loadingText={t('weDidNotMeet')}
                >
                  {t('weDidNotMeet')}
                </Button>
                <Button
                  onClick={weMet}
                  size='medium'
                  disabled={createMatchFeedback.isPending}
                  loading={createMatchFeedback.isPending}
                  loadingText={t('weMet')}
                >
                  {t('weMet')}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HaveYouMetDialog;
